/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/shared/types';

export const hrHR: LocalizationResource = {
  locale: 'hr-HR',
  apiKeys: {
    action__add: 'Dodaj novi ključ',
    action__search: 'Pretraži ključeve',
    copySecret: {
      formButtonPrimary__copyAndClose: 'Kopiraj i zatvori',
      formHint: 'Iz sigurnosnih razloga, nećemo vam dopustiti da ga ponovo pogledate kasnije.',
      formTitle: 'Kopirajte svoj API ključ "{{name}}" sada',
    },
    createdAndExpirationStatus__expiresOn:
      "Kreiran {{ createdDate | shortDate('hr-HR') }} • Istječe {{ expiresDate | longDate('hr-HR') }}",
    createdAndExpirationStatus__never: "Kreiran {{ createdDate | shortDate('hr-HR') }} • Nikad ne istječe",
    detailsTitle__emptyRow: 'Nisu pronađeni API ključevi',
    formButtonPrimary__add: 'Kreiraj ključ',
    formFieldCaption__expiration__expiresOn: 'Istječe {{ date }}',
    formFieldCaption__expiration__never: 'Ovaj ključ nikad neće isteći',
    formFieldOption__expiration__180d: '180 dana',
    formFieldOption__expiration__1d: '1 dan',
    formFieldOption__expiration__1y: '1 godina',
    formFieldOption__expiration__30d: '30 dana',
    formFieldOption__expiration__60d: '60 dana',
    formFieldOption__expiration__7d: '7 dana',
    formFieldOption__expiration__90d: '90 dana',
    formFieldOption__expiration__never: 'Nikada',
    formHint: 'Unesite naziv za generiranje novog ključa. Moći ćete ga opozvati u bilo kojem trenutku.',
    formTitle: 'Dodaj novi API ključ',
    lastUsed__days: 'prije {{days}}d',
    lastUsed__hours: 'prije {{hours}}h',
    lastUsed__minutes: 'prije {{minutes}}m',
    lastUsed__months: 'prije {{months}}mj',
    lastUsed__seconds: 'prije {{seconds}}s',
    lastUsed__years: 'prije {{years}}g',
    menuAction__revoke: 'Opozovi ključ',
    revokeConfirmation: {
      confirmationText: 'Opozovi',
      formButtonPrimary__revoke: 'Opozovi ključ',
      formHint: 'Jeste li sigurni da želite izbrisati ovaj tajni ključ?',
      formTitle: 'Opozovi tajni ključ "{{apiKeyName}}"?',
    },
  },
  backButton: 'Natrag',
  badge__activePlan: 'Aktivan',
  badge__canceledEndsAt: "Otkazan • Završava {{ date | shortDate('hr-HR') }}",
  badge__currentPlan: 'Trenutni plan',
  badge__default: 'Zadano',
  badge__endsAt: "Završava {{ date | shortDate('hr-HR') }}",
  badge__expired: 'Isteklo',
  badge__freeTrial: 'Besplatno probno razdoblje',
  badge__otherImpersonatorDevice: 'Drugi uređaj za oponašanje',
  badge__pastDueAt: "Dospjelo {{ date | shortDate('hr-HR') }}",
  badge__pastDuePlan: 'Dospjelo',
  badge__primary: 'Primarno',
  badge__renewsAt: "Obnavlja se {{ date | shortDate('hr-HR') }}",
  badge__requiresAction: 'Zahtijeva akciju',
  badge__startsAt: "Počinje {{ date | shortDate('hr-HR') }}",
  badge__thisDevice: 'Ovaj uređaj',
  badge__trialEndsAt: "Probno razdoblje završava {{ date | shortDate('hr-HR') }}",
  badge__unverified: 'Nepotvrđeno',
  badge__upcomingPlan: 'Nadolazeći',
  badge__userDevice: 'Korisnički uređaj',
  badge__you: 'Vi',
  billing: {
    addPaymentMethod__label: 'Dodajte način plaćanja',
    alwaysFree: 'Uvijek besplatno',
    annually: 'Godišnje',
    availableFeatures: 'Dostupne značajke',
    billedAnnually: 'Naplaćuje se godišnje',
    billedMonthlyOnly: 'Naplaćuje se samo mjesečno',
    billedAnnuallyOnly: 'Naplaćuje se samo godišnje',
    cancelFreeTrial: 'Otkažite probno razdoblje',
    cancelFreeTrialAccessUntil:
      "Vaše probno razdoblje ostat će aktivno do {{ date | longDate('hr-HR') }}. Nakon toga izgubit ćete pristup značajkama probnog razdoblja. Nećete biti naplaćeni.",
    cancelFreeTrialTitle: 'Otkažite probno razdoblje za plan {{ plan }}?',
    cancelSubscription: 'Otkaži pretplatu',
    cancelSubscriptionAccessUntil:
      "Možete nastaviti koristiti značajke '{{plan}}' do {{ date | longDate('hr-HR') }}, nakon čega više nećete imati pristup.",
    cancelSubscriptionNoCharge: 'Nećete biti naplaćeni za ovu pretplatu.',
    cancelSubscriptionPastDue:
      'Vaša pretplata će odmah završiti i izgubit ćete pristup svim značajkama plana. Bit ćete zamoljeni da platite dospjeli iznos pri sljedećoj pretplati.',
    cancelSubscriptionTitle: 'Otkazati pretplatu na {{plan}}?',
    cannotSubscribeMonthly:
      'Ne možete se pretplatiti na ovaj plan plaćajući mjesečno. Za pretplatu na ovaj plan trebate odabrati godišnje plaćanje.',
    cannotSubscribeUnrecoverable:
      'Ne možete se pretplatiti na ovaj plan. Vaša postojeća pretplata je skuplja od ovog plana.',
    checkout: {
      description__paymentSuccessful: 'Vaše plaćanje je uspješno.',
      description__subscriptionSuccessful: 'Vaša nova pretplata je spremna.',
      downgradeNotice:
        'Zadržat ćete svoju trenutnu pretplatu i njezine značajke do kraja obračunskog razdoblja, nakon čega ćete biti prebačeni na ovu pretplatu.',
      emailForm: {
        subtitle: 'Prije nego što dovršite kupnju, morate dodati e-mail adresu na koju će se slati računi.',
        title: 'Dodajte e-mail adresu',
      },
      lineItems: {
        title__freeTrialEndsAt: 'Probno razdoblje završava',
        title__paymentMethod: 'Način plaćanja',
        title__statementId: 'ID izvoda',
        title__subscriptionBegins: 'Pretplata počinje',
        title__totalPaid: 'Ukupno plaćeno',
      },
      pastDueNotice: 'Vaša prethodna pretplata je bila dospjela, bez plaćanja.',
      perMonth: 'mjesečno',
      title: 'Naplata',
      title__paymentSuccessful: 'Plaćanje je uspjelo!',
      title__subscriptionSuccessful: 'Uspjeh!',
      title__trialSuccess: 'Probno razdoblje uspješno pokrenuto!',
      totalDueAfterTrial: 'Ukupno za platiti nakon isteka probnog razdoblja za {{days}} dana',
    },
    credit: 'Kredit',
    prorationCredit: 'Proporcionalni kredit',
    accountCredit: 'Kredit računa',
    creditRemainder: 'Kredit za preostalo razdoblje vaše trenutne pretplate.',
    payerCreditRemainder: 'Kredit sa stanja računa.',
    defaultFreePlanActive: 'Trenutno ste na besplatnom planu',
    free: 'Besplatno',
    getStarted: 'Započnite',
    keepFreeTrial: 'Zadrži probno razdoblje',
    keepSubscription: 'Zadrži pretplatu',
    manage: 'Upravljaj',
    manageSubscription: 'Upravljaj pretplatom',
    month: 'Mjesec',
    monthAbbreviation: 'mj',
    monthPerUnit: 'Mjesec po {{unitName}}',
    monthly: 'Mjesečno',
    pastDue: 'Dospjelo',
    pay: 'Plati {{amount}}',
    paymentMethod: {
      applePayDescription: {
        annual: 'Godišnje plaćanje',
        monthly: 'Mjesečno plaćanje',
      },
      dev: {
        anyNumbers: 'Bilo koji brojevi',
        cardNumber: 'Broj kartice',
        cvcZip: 'CVC, poštanski broj',
        developmentMode: 'Razvojni način',
        expirationDate: 'Datum isteka',
        testCardInfo: 'Podaci testne kartice',
      },
    },
    paymentMethods__label: 'Načini plaćanja',
    highlightedPlanBadge: 'Popularno',
    pricingTable: {
      billingCycle: 'Obračunsko razdoblje',
      included: 'Uključeno',
      seatCost: {
        freeUpToSeats: 'Besplatno do {{endsAfterBlock}} mjesta',
        upToSeats: 'Do {{endsAfterBlock}} mjesta',
        perSeat: '{{feePerBlockAmount}}/{{periodAbbreviation}} po mjestu',
        includedSeats: '{{includedSeats}} mjesta uključeno',
        additionalSeats: '({{additionalTierFeePerBlockAmount}}/{{periodAbbreviation}} za dodatna)',
        unlimitedSeats: 'Neograničen broj mjesta',
        tooltip: {
          freeForUpToSeats: 'Besplatno za do {{endsAfterBlock}} mjesta.',
          additionalSeatsEach: 'Dodatna mjesta su {{feePerBlockAmount}}/{{period}} svako.',
          firstSeatsIncludedInPlan: 'Prvih {{endsAfterBlock}} mjesta uključeno je u plan.',
        },
      },
    },
    reSubscribe: 'Ponovno se pretplatite',
    seats: 'Mjesta',
    seeAllFeatures: 'Pogledaj sve značajke',
    startFreeTrial: 'Pokreni probno razdoblje',
    startFreeTrial__days: 'Pokreni {{days}}-dnevno probno razdoblje',
    subscribe: 'Pretplatite se',
    subscriptionDetails: {
      beginsOn: 'Počinje',
      currentBillingCycle: 'Trenutno obračunsko razdoblje',
      endsOn: 'Završava',
      firstPaymentAmount: 'Iznos prvog plaćanja',
      firstPaymentOn: 'Prvo plaćanje',
      nextPaymentAmount: 'Iznos sljedećeg plaćanja',
      nextPaymentOn: 'Sljedeće plaćanje',
      pastDueAt: 'Dospjelo',
      renewsAt: 'Obnavlja se',
      subscribedOn: 'Pretplaćeno',
      title: 'Pretplata',
      trialEndsOn: 'Probno razdoblje završava',
      trialStartedOn: 'Probno razdoblje pokrenuto',
    },
    subtotal: 'Međuzbroj',
    switchPlan: 'Prebaci na ovaj plan',
    switchToAnnual: 'Prebaci na godišnje',
    switchToAnnualWithAnnualPrice: 'Prebaci na godišnje {{currency}}{{price}} / godišnje',
    switchToMonthly: 'Prebaci na mjesečno',
    switchToMonthlyWithPrice: 'Prebaci na mjesečno {{currency}}{{price}} / mjesečno',
    totalDue: 'Ukupno za platiti',
    totalDueToday: 'Ukupno za platiti danas',
    viewFeatures: 'Pogledaj značajke',
    viewPayment: 'Pogledaj plaćanje',
    year: 'Godina',
    yearAbbreviation: 'god',
    yearPerUnit: 'Godina po {{unitName}}',
  },
  configureSSO: {
    missingManageEnterpriseConnectionsPermission: {
      subtitle: 'Obratite se administratoru vaše organizacije kako biste nadogradili svoja dopuštenja.',
      title: 'Nemate dopuštenje za upravljanje jedinstvenom prijavom (SSO)',
    },
    navbar: {
      title: 'Konfiguriraj jedinstvenu prijavu (SSO)',
    },
    selectProviderStep: {
      title: 'Odaberite pružatelja',
      subtitle: 'Odaberite pružatelja za kojeg ćete postaviti SSO.',
      saml: {
        groupLabel: 'SAML',
        okta: 'Okta Workforce',
        customSaml: 'Prilagođeni SAML pružatelj',
      },
      warning: 'Nakon odabira pružatelja ne možete ga ponovno mijenjati dok konfiguracija ne završi',
    },
    verifyEmailDomainStep: {
      title: 'Potvrdi e-mail adresu',
      subtitle: 'Potvrdite e-mail adresu na kojoj želite omogućiti poslovnu vezu.',
      addEmailAddress: {
        formTitle: 'Treba nam vaš e-mail',
        formSubtitle: 'Za početak ćemo trebati vašu e-mail adresu',
        inputPlaceholder: 'name@company.com',
        inputLabel: 'E-mail adresa',
      },
      emailCode: {
        formTitle: 'Potvrdite svoju e-mail adresu',
        formSubtitle: 'Unesite verifikacijski kod poslan na {{identifier}}',
        resendButton: 'Niste primili kod? Pošalji ponovno',
        verified: {
          title: 'Primili smo vaš e-mail',
          subtitle: 'Potvrdili ste svoju e-mail adresu sljedećim e-mailom',
          inputLabel: 'Potvrđena e-mail adresa',
        },
      },
      domainTaken: {
        title: 'Ova domena ({{domain}}) već ima SSO vezu',
        subtitle: 'Kontaktirajte administratora aplikacije kako biste dobili pristup putem postojeće veze.',
      },
    },
  },
  createOrganization: {
    formButtonSubmit: 'kreiraj organizaciju',
    invitePage: {
      formButtonReset: 'Preskoči',
    },
    title: 'Kreiraj organizaciju',
  },
  dates: {
    lastDay: "Jučer u {{ date | timeString('hr-HR') }}",
    next6Days: "{{ date | weekday('hr-HR','long') }} u {{ date | timeString('hr-HR') }}",
    nextDay: "Sutra u {{ date | timeString('hr-HR') }}",
    numeric: "{{ date | numeric('hr-HR') }}",
    previous6Days: "Prošli {{ date | weekday('hr-HR','long') }} u {{ date | timeString('hr-HR') }}",
    sameDay: "Danas u {{ date | timeString('hr-HR') }}",
  },
  dividerText: 'ili',
  footerActionLink__alternativePhoneCodeProvider: 'Pošalji kod putem SMS-a',
  footerActionLink__useAnotherMethod: 'Koristite drugu metodu',
  footerPageLink__help: 'Pomoć',
  footerPageLink__privacy: 'Privatnost',
  footerPageLink__terms: 'Uvjeti',
  formButtonPrimary: 'Nastavi',
  formButtonPrimary__verify: 'Potvrdi',
  formFieldAction__forgotPassword: 'Zaboravili ste lozinku?',
  formFieldError__matchingPasswords: 'Lozinke se podudaraju.',
  formFieldError__notMatchingPasswords: 'Lozinke se ne podudaraju.',
  formFieldError__verificationLinkExpired: 'Verifikacijska poveznica je istekla. Molimo zatražite novu poveznicu.',
  formFieldHintText__optional: 'Neobavezno',
  formFieldHintText__slug: 'Slug je čitljiv ID koji mora biti jedinstven. Često se koristi u URL-ovima.',
  formFieldInputPlaceholder__apiKeyDescription: 'Objasnite zašto generirate ovaj ključ',
  formFieldInputPlaceholder__apiKeyExpirationDate: 'Odaberite datum',
  formFieldInputPlaceholder__apiKeyName: 'Unesite naziv tajnog ključa',
  formFieldInputPlaceholder__backupCode: 'Unesite rezervni kod',
  formFieldInputPlaceholder__confirmDeletionUserAccount: 'Izbriši račun',
  formFieldInputPlaceholder__emailAddress: 'Unesite svoju e-mail adresu',
  formFieldInputPlaceholder__emailAddress_username: 'Unesite e-mail ili korisničko ime',
  formFieldInputPlaceholder__emailAddresses: 'primjer@email.com, primjer2@email.com',
  formFieldInputPlaceholder__firstName: 'Ime',
  formFieldInputPlaceholder__lastName: 'Prezime',
  formFieldInputPlaceholder__organizationDomain: 'primjer.com',
  formFieldInputPlaceholder__organizationDomainEmailAddress: 'vi@primjer.com',
  formFieldInputPlaceholder__organizationName: 'Naziv organizacije',
  formFieldInputPlaceholder__organizationSlug: 'moja-organizacija',
  formFieldInputPlaceholder__password: 'Unesite svoju lozinku',
  formFieldInputPlaceholder__signUpPassword: 'Kreirajte lozinku',
  formFieldInputPlaceholder__phoneNumber: 'Unesite svoj telefonski broj',
  formFieldInputPlaceholder__username: 'Unesite svoje korisničko ime',
  formFieldInput__emailAddress_format: 'Primjer formata: ime@primjer.com',
  formFieldLabel__apiKey: 'API ključ',
  formFieldLabel__apiKeyDescription: 'Opis',
  formFieldLabel__apiKeyExpiration: 'Istječe',
  formFieldLabel__apiKeyName: 'Naziv tajnog ključa',
  formFieldLabel__automaticInvitations: 'Omogući automatske pozivnice za ovu domenu',
  formFieldLabel__backupCode: 'Rezervni kod',
  formFieldLabel__confirmDeletion: 'Potvrda',
  formFieldLabel__confirmPassword: 'Potvrdi lozinku',
  formFieldLabel__currentPassword: 'Trenutna lozinka',
  formFieldLabel__emailAddress: 'E-mail adresa',
  formFieldLabel__emailAddress_username: 'E-mail adresa ili korisničko ime',
  formFieldLabel__emailAddresses: 'E-mail adrese',
  formFieldLabel__firstName: 'Ime',
  formFieldLabel__lastName: 'Prezime',
  formFieldLabel__newPassword: 'Nova lozinka',
  formFieldLabel__organizationDomain: 'Domena',
  formFieldLabel__organizationDomainDeletePending: 'Izbriši prijedloge i pozivnice na čekanju',
  formFieldLabel__organizationDomainEmailAddress: 'E-mail adresa za verifikaciju',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Unesite e-mail adresu pod ovom domenom kako biste primili kod i potvrdili ovu domenu.',
  formFieldLabel__organizationName: 'Naziv',
  formFieldLabel__organizationSlug: 'Slug',
  formFieldLabel__passkeyName: 'Naziv pristupnog ključa',
  formFieldLabel__password: 'Lozinka',
  formFieldLabel__phoneNumber: 'Telefonski broj',
  formFieldLabel__role: 'Uloga',
  formFieldLabel__signOutOfOtherSessions: 'Odjavi se sa svih ostalih uređaja',
  formFieldLabel__username: 'Korisničko ime',
  impersonationFab: {
    action__signOut: 'Odjava',
    title: 'Prijavljeni ste kao {{identifier}}',
  },
  lastAuthenticationStrategy: 'Zadnje korišteno',
  maintenanceMode: 'Trenutno provodimo održavanje, ali ne brinite, ne bi trebalo trajati duže od nekoliko minuta.',
  membershipRole__admin: 'Administrator',
  membershipRole__basicMember: 'Član',
  membershipRole__guestMember: 'Gost',
  organizationList: {
    action__createOrganization: 'Kreiraj organizaciju',
    action__invitationAccept: 'Pridruži se',
    action__suggestionsAccept: 'Zatraži pridruživanje',
    createOrganization: 'Kreiraj organizaciju',
    invitationAcceptedLabel: 'Pridružen',
    subtitle: 'za nastavak na {{applicationName}}',
    suggestionsAcceptedLabel: 'Čeka odobrenje',
    title: 'Odaberite račun',
    titleWithoutPersonal: 'Odaberite organizaciju',
  },
  organizationProfile: {
    apiKeysPage: {
      title: 'API ključevi',
    },
    badge__automaticInvitation: 'Automatske pozivnice',
    badge__automaticSuggestion: 'Automatski prijedlozi',
    badge__manualInvitation: 'Bez automatskog učlanjenja',
    badge__unverified: 'Nepotvrđeno',
    billingPage: {
      paymentHistorySection: {
        empty: 'Nema povijesti plaćanja',
        notFound: 'Pokušaj plaćanja nije pronađen',
        tableHeader__amount: 'Iznos',
        tableHeader__date: 'Datum',
        tableHeader__status: 'Status',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Postavi kao zadano',
        actionLabel__remove: 'Ukloni',
        add: 'Dodaj novi način plaćanja',
        addSubtitle: 'Dodajte novi način plaćanja na svoj račun.',
        cancelButton: 'Odustani',
        formButtonPrimary__add: 'Dodaj način plaćanja',
        formButtonPrimary__pay: 'Plati {{amount}}',
        payWithTestCardButton: 'Plati testnom karticom',
        removeMethod: {
          messageLine1: '{{identifier}} će biti uklonjen s ovog računa.',
          messageLine2:
            'Više nećete moći koristiti ovaj način plaćanja i sve ponavljajuće pretplate koje o njemu ovise više neće raditi.',
          successMessage: '{{paymentMethod}} je uklonjen s vašeg računa.',
          title: 'Ukloni način plaćanja',
        },
        title: 'Načini plaćanja',
      },
      start: {
        headerTitle__payments: 'Plaćanja',
        headerTitle__plans: 'Planovi',
        headerTitle__statements: 'Izvodi',
        headerTitle__subscriptions: 'Pretplata',
      },
      statementsSection: {
        empty: 'Nema izvoda za prikaz',
        itemCaption__paidForPlan: 'Plaćeno za {{plan}} {{period}} plan',
        itemCaption__proratedCredit: 'Proporcionalni kredit za djelomično korištenje prethodne pretplate',
        itemCaption__payerCredit: 'Kredit sa stanja računa',
        itemCaption__subscribedAndPaidForPlan: 'Pretplaćeno i plaćeno za {{plan}} {{period}} plan',
        notFound: 'Izvod nije pronađen',
        tableHeader__amount: 'Iznos',
        tableHeader__date: 'Datum',
        title: 'Izvodi',
        totalPaid: 'Ukupno plaćeno',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Upravljaj',
        actionLabel__newSubscription: 'Pretplatite se na plan',
        actionLabel__switchPlan: 'Promijeni plan',
        tableHeader__edit: 'Uredi',
        tableHeader__plan: 'Plan',
        tableHeader__startDate: 'Datum početka',
        title: 'Pretplata',
      },
      subscriptionsSection: {
        actionLabel__default: 'Upravljaj',
      },
      switchPlansSection: {
        title: 'Promijeni plan',
      },
      title: 'Naplata',
    },
    createDomainPage: {
      subtitle:
        'Dodajte domenu za provjeru. Korisnici s e-mail adresama na ovoj domeni mogu se automatski pridružiti organizaciji ili zatražiti pridruživanje.',
      title: 'Dodaj domenu',
    },
    invitePage: {
      detailsTitle__inviteFailed:
        'Pozivnice se nisu mogle poslati. Već postoje pozivnice na čekanju za sljedeće e-mail adrese: {{email_addresses}}.',
      formButtonPrimary__continue: 'Pošalji pozivnice',
      selectDropdown__role: 'Odaberi ulogu',
      subtitle: 'Unesite ili zalijepite jednu ili više e-mail adresa, odvojenih razmakom ili zarezom.',
      successMessage: 'Pozivnice uspješno poslane',
      title: 'Pozovi nove članove',
    },
    membersPage: {
      action__invite: 'Pozovi',
      action__search: 'Pretraži',
      activeMembersTab: {
        menuAction__remove: 'Ukloni člana',
        tableHeader__actions: 'Akcije',
        tableHeader__joined: 'Pridružen',
        tableHeader__role: 'Uloga',
        tableHeader__user: 'Korisnik',
      },
      alerts: {
        roleSetMigrationInProgress: {
          subtitle: 'Ažuriramo dostupne uloge. Kada to završimo, moći ćete ponovno ažurirati uloge.',
          title: 'Uloge su privremeno zaključane',
        },
      },
      detailsTitle__emptyRow: 'Nema članova za prikaz',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Pozovite korisnike povezivanjem e-mail domene s vašom organizacijom. Svatko tko se prijavi s odgovarajućom e-mail domenom moći će se pridružiti organizaciji u bilo kojem trenutku.',
          headerTitle: 'Automatske pozivnice',
          primaryButton: 'Upravljaj potvrđenim domenama',
        },
        table__emptyRow: 'Nema pozivnica za prikaz',
      },
      invitedMembersTab: {
        menuAction__revoke: 'Opozovi pozivnicu',
        tableHeader__invited: 'Pozvan',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Korisnici koji se prijave s odgovarajućom e-mail domenom moći će vidjeti prijedlog za zahtjev za pridruživanje vašoj organizaciji.',
          headerTitle: 'Automatski prijedlozi',
          primaryButton: 'Upravljaj potvrđenim domenama',
        },
        menuAction__approve: 'Odobri',
        menuAction__reject: 'Odbij',
        tableHeader__requested: 'Zatražen pristup',
        table__emptyRow: 'Nema zahtjeva za prikaz',
      },
      start: {
        headerTitle__invitations: 'Pozivnice',
        headerTitle__members: 'Članovi',
        headerTitle__requests: 'Zahtjevi',
      },
    },
    navbar: {
      apiKeys: 'API ključevi',
      billing: 'Naplata',
      description: 'Upravljajte svojom organizacijom.',
      general: 'Općenito',
      members: 'Članovi',
      title: 'Organizacija',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: 'Nemate dozvole za upravljanje naplatom za ovu organizaciju.',
        planMembershipLimitExceeded:
          'Vaša organizacija ima {{count}} članova (uključujući pozivnice na čekanju). Ovaj plan dopušta samo {{limit}} članova.',
      },
      title: 'Planovi',
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: 'Upišite "{{organizationName}}" ispod za nastavak.',
          messageLine1: 'Jeste li sigurni da želite izbrisati ovu organizaciju?',
          messageLine2: 'Ova radnja je trajna i nepovratna.',
          successMessage: 'Izbrisali ste organizaciju.',
          title: 'Izbriši organizaciju',
        },
        leaveOrganization: {
          actionDescription: 'Upišite "{{organizationName}}" ispod za nastavak.',
          messageLine1:
            'Jeste li sigurni da želite napustiti ovu organizaciju? Izgubit ćete pristup ovoj organizaciji i njezinim aplikacijama.',
          messageLine2: 'Ova radnja je trajna i nepovratna.',
          successMessage: 'Napustili ste organizaciju.',
          title: 'Napusti organizaciju',
        },
        title: 'Opasnost',
      },
      domainSection: {
        menuAction__manage: 'Upravljaj',
        menuAction__remove: 'Izbriši',
        menuAction__verify: 'Potvrdi',
        primaryButton: 'Dodaj domenu',
        subtitle:
          'Dopustite korisnicima da se automatski pridruže organizaciji ili zatraže pridruživanje na temelju potvrđene e-mail domene.',
        title: 'Potvrđene domene',
      },
      successMessage: 'Organizacija je ažurirana.',
      title: 'Ažuriraj profil',
    },
    removeDomainPage: {
      messageLine1: 'E-mail domena {{domain}} bit će uklonjena.',
      messageLine2: 'Korisnici se nakon ovoga neće moći automatski pridružiti organizaciji.',
      successMessage: '{{domain}} je uklonjena.',
      title: 'Ukloni domenu',
    },
    start: {
      headerTitle__general: 'Općenito',
      headerTitle__members: 'Članovi',
      membershipSeatUsageLabel: '{{count}} od {{limit}} mjesta iskorišteno',
      profileSection: {
        primaryButton: 'Ažuriraj profil',
        title: 'Profil organizacije',
        uploadAction__title: 'Logotip',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Uklanjanje ove domene utjecat će na pozvane korisnike.',
        removeDomainActionLabel__remove: 'Ukloni domenu',
        removeDomainSubtitle: 'Ukloni ovu domenu iz vaših potvrđenih domena',
        removeDomainTitle: 'Ukloni domenu',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Korisnici su automatski pozvani da se pridruže organizaciji kada se prijave i mogu se pridružiti u bilo kojem trenutku.',
        automaticInvitationOption__label: 'Automatske pozivnice',
        automaticSuggestionOption__description:
          'Korisnici primaju prijedlog za zahtjev za pridruživanje, ali moraju biti odobreni od strane administratora prije nego što se mogu pridružiti organizaciji.',
        automaticSuggestionOption__label: 'Automatski prijedlozi',
        calloutInfoLabel: 'Promjena načina upisa utjecat će samo na nove korisnike.',
        calloutInvitationCountLabel: 'Pozivnice na čekanju poslane korisnicima: {{count}}',
        calloutSuggestionCountLabel: 'Prijedlozi na čekanju poslani korisnicima: {{count}}',
        manualInvitationOption__description: 'Korisnici mogu biti pozvani u organizaciju samo ručno.',
        manualInvitationOption__label: 'Bez automatskog upisa',
        subtitle: 'Odaberite kako se korisnici s ove domene mogu pridružiti organizaciji.',
      },
      start: {
        headerTitle__danger: 'Opasnost',
        headerTitle__enrollment: 'Opcije upisa',
      },
      subtitle: 'Domena {{domain}} je sada potvrđena. Nastavite odabirom načina upisa.',
      title: 'Ažuriraj {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Unesite verifikacijski kod poslan na vašu e-mail adresu',
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'Domena {{domainName}} mora biti potvrđena putem e-maila.',
      subtitleVerificationCodeScreen: 'Verifikacijski kod poslan je na {{emailAddress}}. Unesite kod za nastavak.',
      title: 'Potvrdi domenu',
    },
  },
  organizationSwitcher: {
    action__closeOrganizationSwitcher: 'Zatvori prebacivač organizacija',
    action__createOrganization: 'Kreiraj organizaciju',
    action__invitationAccept: 'Pridruži se',
    action__manageOrganization: 'Upravljaj',
    action__openOrganizationSwitcher: 'Otvori prebacivač organizacija',
    action__suggestionsAccept: 'Zatraži pridruživanje',
    notSelected: 'Nije odabrana organizacija',
    personalWorkspace: 'Osobni račun',
    suggestionsAcceptedLabel: 'Čeka odobrenje',
  },
  paginationButton__next: 'Sljedeće',
  paginationButton__previous: 'Prethodno',
  paginationRowText__displaying: 'Prikazuje se',
  paginationRowText__of: 'od',
  reverification: {
    alternativeMethods: {
      actionLink: 'Zatražite pomoć',
      actionText: 'Nemate ništa od ovoga?',
      blockButton__backupCode: 'Koristite rezervni kod',
      blockButton__emailCode: 'Pošalji kod e-poštom na {{identifier}}',
      blockButton__passkey: 'Koristite svoj pristupni ključ',
      blockButton__password: 'Nastavite s vašom lozinkom',
      blockButton__phoneCode: 'Pošalji SMS kod na {{identifier}}',
      blockButton__totp: 'Koristite vašu aplikaciju za autentifikaciju',
      getHelp: {
        blockButton__emailSupport: 'Pošalji e-poštu podršci',
        content:
          'Ako imate problema s verifikacijom vašeg računa, pošaljite nam e-poštu i radit ćemo s vama na vraćanju pristupa što je prije moguće.',
        title: 'Zatražite pomoć',
      },
      subtitle: 'Imate problema? Možete koristiti bilo koju od ovih metoda za verifikaciju.',
      title: 'Koristite drugu metodu',
    },
    backupCodeMfa: {
      subtitle: 'Vaš rezervni kod je onaj koji ste dobili prilikom postavljanja dvostupanjske autentifikacije.',
      title: 'Unesite rezervni kod',
    },
    emailCode: {
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'Unesite kod poslan na vašu e-poštu za nastavak',
      title: 'Potrebna verifikacija',
    },
    noAvailableMethods: {
      message: 'Ne može se nastaviti s verifikacijom. Nema dostupnog faktora autentifikacije.',
      subtitle: 'Došlo je do pogreške',
      title: 'Ne može se verificirati vaš račun',
    },
    passkey: {
      blockButton__passkey: 'Koristite svoj pristupni ključ',
      subtitle:
        'Korištenje vašeg pristupnog ključa potvrđuje vaš identitet. Vaš uređaj može tražiti otisak prsta, prepoznavanje lica ili zaključavanje zaslona.',
      title: 'Koristite svoj pristupni ključ',
    },
    password: {
      actionLink: 'Koristite drugu metodu',
      subtitle: 'Unesite svoju trenutnu lozinku za nastavak',
      title: 'Potrebna verifikacija',
    },
    phoneCode: {
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'Unesite kod poslan na vaš telefon za nastavak',
      title: 'Potrebna verifikacija',
    },
    phoneCodeMfa: {
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'Unesite kod poslan na vaš telefon za nastavak',
      title: 'Potrebna verifikacija',
    },
    totpMfa: {
      formTitle: 'Verifikacijski kod',
      subtitle: 'Unesite kod generiran vašom aplikacijom za autentifikaciju za nastavak',
      title: 'Potrebna verifikacija',
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Dodaj račun',
      action__signOutAll: 'Odjavi se sa svih računa',
      subtitle: 'Odaberite račun s kojim želite nastaviti.',
      title: 'Odaberite račun',
    },
    alternativeMethods: {
      actionLink: 'Zatražite pomoć',
      actionText: 'Nemate ništa od ovoga?',
      blockButton__backupCode: 'Koristite rezervni kod',
      blockButton__emailCode: 'Pošalji kod e-poštom na {{identifier}}',
      blockButton__emailLink: 'Pošalji poveznicu e-poštom na {{identifier}}',
      blockButton__passkey: 'Prijavite se svojim pristupnim ključem',
      blockButton__password: 'Prijavite se svojom lozinkom',
      blockButton__phoneCode: 'Pošalji SMS kod na {{identifier}}',
      blockButton__totp: 'Koristite svoju aplikaciju za autentifikaciju',
      getHelp: {
        blockButton__emailSupport: 'Pošaljite e-poštu podršci',
        content:
          'Ako imate problema s prijavom na svoj račun, pošaljite nam e-poštu i radit ćemo s vama na vraćanju pristupa što je prije moguće.',
        title: 'Zatražite pomoć',
      },
      subtitle: 'Imate problema? Možete koristiti bilo koju od ovih metoda za prijavu.',
      title: 'Koristite drugu metodu',
    },
    alternativePhoneCodeProvider: {
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'za nastavak na {{applicationName}}',
      title: 'Provjerite svoj {{provider}}',
    },
    backupCodeMfa: {
      subtitle: 'Vaš rezervni kod je onaj koji ste dobili prilikom postavljanja dvostupanjske autentifikacije.',
      title: 'Unesite rezervni kod',
    },
    emailCode: {
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'za nastavak na {{applicationName}}',
      title: 'Provjerite svoju e-poštu',
    },
    emailCodeMfa: {
      formTitle: 'Provjerite svoju e-poštu',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'za nastavak na {{applicationName}}',
      title: 'Provjerite svoju e-poštu',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Za nastavak, otvorite verifikacijsku poveznicu na uređaju i pregledniku s kojeg ste započeli prijavu',
        title: 'Verifikacijska poveznica nije valjana za ovaj uređaj',
      },
      expired: {
        subtitle: 'Vratite se na izvornu karticu za nastavak.',
        title: 'Ova verifikacijska poveznica je istekla',
      },
      failed: {
        subtitle: 'Vratite se na izvornu karticu za nastavak.',
        title: 'Ova verifikacijska poveznica nije valjana',
      },
      formSubtitle: 'Koristite verifikacijsku poveznicu poslanu na vašu e-poštu',
      formTitle: 'Verifikacijska poveznica',
      loading: {
        subtitle: 'Uskoro ćete biti preusmjereni',
        title: 'Prijava...',
      },
      resendButton: 'Niste primili poveznicu? Pošalji ponovno',
      subtitle: 'za nastavak na {{applicationName}}',
      title: 'Provjerite svoju e-poštu',
      unusedTab: {
        title: 'Možete zatvoriti ovu karticu',
      },
      verified: {
        subtitle: 'Uskoro ćete biti preusmjereni',
        title: 'Uspješno prijavljeni',
      },
      verifiedSwitchTab: {
        subtitle: 'Vratite se na izvornu karticu za nastavak',
        subtitleNewTab: 'Vratite se na novootvorenu karticu za nastavak',
        titleNewTab: 'Prijavljeni na drugoj kartici',
      },
    },
    emailLinkMfa: {
      formSubtitle: 'Koristite vezu za provjeru poslanu na vašu e-poštu',
      resendButton: 'Niste primili vezu? Ponovno pošalji',
      subtitle: 'za nastavak na {{applicationName}}',
      title: 'Provjerite svoju e-poštu',
    },
    enterpriseConnections: {
      subtitle: 'Odaberite poslovni račun s kojim želite nastaviti.',
      title: 'Odaberite svoj poslovni račun',
    },
    forgotPassword: {
      formTitle: 'Kod za resetiranje lozinke',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'za resetiranje vaše lozinke',
      subtitle_email: 'Prvo unesite kod poslan na vašu e-mail adresu',
      subtitle_phone: 'Prvo unesite kod poslan na vaš telefon',
      title: 'Resetiraj lozinku',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: 'Resetirajte svoju lozinku',
      label__alternativeMethods: 'Ili se prijavite na drugi način',
      title: 'Zaboravili ste lozinku?',
    },
    newDeviceVerificationNotice:
      'Prijavljujete se s novog uređaja. Tražimo provjeru kako bismo zadržali vaš račun sigurnim.',
    noAvailableMethods: {
      message: 'Nije moguće nastaviti s prijavom. Nema dostupnog faktora autentifikacije.',
      subtitle: 'Došlo je do pogreške',
      title: 'Nije moguće prijaviti se',
    },
    passkey: {
      subtitle:
        'Korištenje vašeg pristupnog ključa potvrđuje da ste to vi. Vaš uređaj može tražiti otisak prsta, prepoznavanje lica ili zaključavanje zaslona.',
      title: 'Koristite svoj pristupni ključ',
    },
    password: {
      actionLink: 'Koristite drugu metodu',
      subtitle: 'Unesite lozinku povezanu s vašim računom',
      title: 'Unesite svoju lozinku',
    },
    passwordCompromised: {
      title: 'Lozinka je kompromitirana',
    },
    passwordPwned: {
      title: 'Lozinka je kompromitirana',
    },
    passwordUntrusted: {
      title: 'Lozinka nije pouzdana',
    },
    phoneCode: {
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'za nastavak na {{applicationName}}',
      title: 'Provjerite svoj telefon',
    },
    phoneCodeMfa: {
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'Za nastavak, unesite verifikacijski kod poslan na vaš telefon',
      title: 'Provjerite svoj telefon',
    },
    resetPassword: {
      formButtonPrimary: 'Resetiraj lozinku',
      requiredMessage: 'Iz sigurnosnih razloga, potrebno je resetirati vašu lozinku.',
      successMessage: 'Vaša lozinka je uspješno promijenjena. Prijavljujemo vas, molimo pričekajte trenutak.',
      title: 'Postavite novu lozinku',
    },
    resetPasswordMfa: {
      detailsLabel: 'Moramo potvrditi vaš identitet prije resetiranja lozinke.',
    },
    start: {
      actionLink: 'Registrirajte se',
      actionLink__join_waitlist: 'Pridružite se listi čekanja',
      actionLink__use_email: 'Koristite e-poštu',
      actionLink__use_email_username: 'Koristite e-poštu ili korisničko ime',
      actionLink__use_passkey: 'Koristite pristupni ključ umjesto toga',
      actionLink__use_phone: 'Koristite telefon',
      actionLink__use_username: 'Koristite korisničko ime',
      actionText: 'Nemate račun?',
      actionText__join_waitlist: 'Želite rani pristup?',
      alternativePhoneCodeProvider: {
        actionLink: 'Koristite drugu metodu',
        label: '{{provider}} telefonski broj',
        subtitle: 'Unesite svoj telefonski broj kako biste dobili verifikacijski kod na {{provider}}.',
        title: 'Prijavite se na {{applicationName}} putem {{provider}}',
      },
      subtitle: 'Dobrodošli natrag! Molimo prijavite se za nastavak',
      subtitleCombined: undefined,
      title: 'Prijavite se na {{applicationName}}',
      titleCombined: 'Nastavite na {{applicationName}}',
    },
    totpMfa: {
      formTitle: 'Verifikacijski kod',
      subtitle: 'Za nastavak, unesite verifikacijski kod generiran vašom aplikacijom za autentifikaciju',
      title: 'Dvostupanjska verifikacija',
    },
    web3Solana: {
      subtitle: 'Odaberite novčanik u nastavku za prijavu',
      title: 'Prijava putem Solane',
    },
  },
  signInEnterPasswordTitle: 'Unesite svoju lozinku',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'Unesite verifikacijski kod poslan na vaš {{provider}}',
      title: 'Potvrdite svoj {{provider}}',
    },
    continue: {
      actionLink: 'Prijavi se',
      actionText: 'Već imate račun?',
      subtitle: 'Molimo ispunite preostale detalje za nastavak.',
      title: 'Ispunite preostala polja',
    },
    emailCode: {
      formSubtitle: 'Unesite verifikacijski kod poslan na vašu e-mail adresu',
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'Unesite verifikacijski kod poslan na vašu e-poštu',
      title: 'Potvrdite svoju e-poštu',
    },
    emailLink: {
      clientMismatch: {
        subtitle:
          'Za nastavak, otvorite verifikacijsku poveznicu na uređaju i pregledniku s kojeg ste započeli registraciju',
        title: 'Verifikacijska poveznica nije valjana za ovaj uređaj',
      },
      formSubtitle: 'Koristite verifikacijsku poveznicu poslanu na vašu e-mail adresu',
      formTitle: 'Verifikacijska poveznica',
      loading: {
        title: 'Registracija...',
      },
      resendButton: 'Niste primili poveznicu? Pošalji ponovno',
      subtitle: 'za nastavak na {{applicationName}}',
      title: 'Potvrdite svoju e-poštu',
      verified: {
        title: 'Uspješno registrirani',
      },
      verifiedSwitchTab: {
        subtitle: 'Vratite se na novootvorenu karticu za nastavak',
        subtitleNewTab: 'Vratite se na prethodnu karticu za nastavak',
        title: 'Uspješno potvrđena e-pošta',
      },
    },
    enterpriseConnections: {
      subtitle: 'Odaberite poslovni račun s kojim želite nastaviti.',
      title: 'Odaberite svoj poslovni račun',
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: 'Slažem se s {{ privacyPolicyLink || link("Politikom privatnosti") }}',
        label__onlyTermsOfService: 'Slažem se s {{ termsOfServiceLink || link("Uvjetima korištenja") }}',
        label__termsOfServiceAndPrivacyPolicy:
          'Slažem se s {{ termsOfServiceLink || link("Uvjetima korištenja") }} i {{ privacyPolicyLink || link("Politikom privatnosti") }}',
      },
      continue: {
        subtitle: 'Molimo pročitajte i prihvatite uvjete kako biste nastavili',
        title: 'Pravni pristanak',
      },
    },
    phoneCode: {
      formSubtitle: 'Unesite verifikacijski kod poslan na vaš broj telefona',
      formTitle: 'Verifikacijski kod',
      resendButton: 'Niste primili kod? Pošalji ponovno',
      subtitle: 'Unesite verifikacijski kod poslan na vaš telefon',
      title: 'Potvrdite svoj telefon',
    },
    restrictedAccess: {
      actionLink: 'Prijavi se',
      actionText: 'Već imate račun?',
      blockButton__emailSupport: 'Pošalji e-poštu podršci',
      blockButton__joinWaitlist: 'Pridružite se listi čekanja',
      subtitle:
        'Registracije su trenutno onemogućene. Ako smatrate da biste trebali imati pristup, obratite se podršci.',
      subtitleWaitlist:
        'Registracije su trenutno onemogućene. Pridružite se listi čekanja kako biste prvi saznali kad pokrenemo.',
      title: 'Pristup ograničen',
    },
    start: {
      actionLink: 'Prijavi se',
      actionLink__use_email: 'Koristite e-poštu',
      actionLink__use_phone: 'Koristite telefon',
      actionText: 'Već imate račun?',
      alternativePhoneCodeProvider: {
        actionLink: 'Koristite drugu metodu',
        label: '{{provider}} telefonski broj',
        subtitle: 'Unesite svoj telefonski broj kako biste dobili verifikacijski kod na {{provider}}.',
        title: 'Registrirajte se na {{applicationName}} putem {{provider}}',
      },
      subtitle: 'Dobrodošli! Molimo ispunite detalje za početak.',
      subtitleCombined: 'Dobrodošli! Molimo ispunite detalje za početak.',
      title: 'Kreirajte svoj račun',
      titleCombined: 'Kreirajte svoj račun',
    },
    web3Solana: {
      subtitle: 'Odaberite novčanik u nastavku za registraciju',
      title: 'Registracija putem Solane',
    },
  },
  socialButtonsBlockButton: 'Nastavite s {{provider|titleize}}',
  socialButtonsBlockButtonManyInView: '{{provider|titleize}}',
  taskChooseOrganization: {
    alerts: {
      organizationAlreadyExists:
        'Organizacija već postoji za otkriveni naziv tvrtke ({{organizationName}}) i {{organizationDomain}}. Pridružite se putem pozivnice.',
    },
    chooseOrganization: {
      action__createOrganization: 'Stvori novu organizaciju',
      action__invitationAccept: 'Pridruži se',
      action__suggestionsAccept: 'Zatraži pridruživanje',
      subtitle: 'Pridružite se postojećoj organizaciji ili stvorite novu',
      subtitle__createOrganizationDisabled: 'Pridružite se postojećoj organizaciji',
      suggestionsAcceptedLabel: 'Čeka odobrenje',
      title: 'Odaberite organizaciju',
    },
    createOrganization: {
      formButtonReset: 'Odustani',
      formButtonSubmit: 'Nastavi',
      formFieldInputPlaceholder__name: 'Moja organizacija',
      formFieldInputPlaceholder__slug: 'moja-organizacija',
      formFieldLabel__name: 'Naziv',
      formFieldLabel__slug: 'Slug',
      subtitle: 'Unesite podatke o svojoj organizaciji za nastavak',
      title: 'Postavite svoju organizaciju',
    },
    organizationCreationDisabled: {
      subtitle: 'Kontaktirajte administratora svoje organizacije za pozivnicu.',
      title: 'Morate pripadati organizaciji',
    },
    signOut: {
      actionLink: 'Odjavi se',
      actionText: 'Prijavljen kao {{identifier}}',
    },
  },
  taskResetPassword: {
    formButtonPrimary: 'Resetiraj lozinku',
    signOut: {
      actionLink: 'Odjavi se',
      actionText: 'Prijavljen kao {{identifier}}',
    },
    subtitle: 'Vaš račun zahtijeva novu lozinku prije nego što možete nastaviti',
    title: 'Resetirajte svoju lozinku',
  },
  taskSetupMfa: {
    badge: 'Postavljanje dvostupanjske verifikacije',
    signOut: {
      actionLink: 'Odjavi se',
      actionText: 'Prijavljen kao {{identifier}}',
    },
    smsCode: {
      addPhone: {
        formButtonPrimary: 'Nastavi',
        infoText:
          'Tekstualna poruka s verifikacijskim kodom bit će poslana na ovaj telefonski broj. Mogu se primijeniti naknade za poruke i podatke.',
      },
      addPhoneNumber: 'Dodaj telefonski broj',
      cancel: 'Odustani',
      subtitle: 'Odaberite telefonski broj koji želite koristiti za dvostupanjsku verifikaciju SMS kodom',
      success: {
        finishButton: 'Nastavi',
        message1:
          'Dvostupanjska verifikacija je sada omogućena. Prilikom prijave, morat ćete unijeti verifikacijski kod poslan na ovaj telefonski broj kao dodatni korak.',
        message2:
          'Spremite ove rezervne kodove i pohranite ih na sigurno mjesto. Ako izgubite pristup svom uređaju za autentifikaciju, možete koristiti rezervne kodove za prijavu.',
        title: 'SMS kod verifikacije omogućen',
      },
      title: 'Dodaj SMS kod verifikacije',
      verifyPhone: {
        formButtonPrimary: 'Nastavi',
        formTitle: 'Verifikacijski kod',
        resendButton: 'Niste primili kod? Pošalji ponovno',
        subtitle: 'Unesite verifikacijski kod poslan na',
        title: 'Potvrdite svoj telefonski broj',
      },
    },
    start: {
      methodSelection: {
        phoneCode: 'SMS kod',
        totp: 'Aplikacija za autentifikaciju',
      },
      subtitle: 'Odaberite metodu kojom želite zaštititi svoj račun s dodatnim slojem sigurnosti',
      title: 'Postavi dvostupanjsku verifikaciju',
    },
    totpCode: {
      addAuthenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Umjesto toga, skeniraj QR kod',
        buttonUnableToScan__nonPrimary: 'Ne možete skenirati QR kod?',
        formButtonPrimary: 'Nastavi',
        formButtonReset: 'Odustani',
        infoText__ableToScan:
          'Postavite novu metodu prijave u vašoj aplikaciji za autentifikaciju i skenirajte sljedeći QR kod da biste je povezali s vašim računom.',
        infoText__unableToScan: 'Postavite novu metodu prijave u vašem autentifikatoru i unesite Ključ naveden ispod.',
        inputLabel__unableToScan1:
          'Provjerite jesu li omogućene vremenski temeljene ili jednokratne lozinke, zatim završite povezivanje vašeg računa.',
      },
      success: {
        finishButton: 'Nastavi',
        message1:
          'Dvostupanjska verifikacija je sada omogućena. Prilikom prijave, morat ćete unijeti verifikacijski kod iz ovog autentifikatora kao dodatni korak.',
        message2:
          'Spremite ove rezervne kodove i pohranite ih na sigurno mjesto. Ako izgubite pristup svom uređaju za autentifikaciju, možete koristiti rezervne kodove za prijavu.',
        title: 'Verifikacija aplikacijom za autentifikaciju omogućena',
      },
      title: 'Dodaj aplikaciju za autentifikaciju',
      verifyTotp: {
        formButtonPrimary: 'Nastavi',
        formButtonReset: 'Odustani',
        formTitle: 'Verifikacijski kod',
        subtitle: 'Unesite verifikacijski kod generiran vašim autentifikatorom',
        title: 'Dodaj aplikaciju za autentifikaciju',
      },
    },
  },
  unstable__errors: {
    already_a_member_in_organization: '{{email}} je već član organizacije.',
    api_key_name_already_exists: 'Naziv API ključa već postoji.',
    api_key_usage_exceeded:
      'Dosegli ste ograničenje korištenja. Možete ukloniti ograničenje nadogradnjom na plaćeni plan.',
    avatar_file_size_exceeded:
      'Veličina datoteke premašuje maksimalno ograničenje od 10 MB. Molimo odaberite manju datoteku.',
    avatar_file_type_invalid: 'Vrsta datoteke nije podržana. Molimo prenesite sliku u formatu JPG, PNG, GIF ili WEBP.',
    captcha_invalid:
      'Registracija neuspješna zbog neuspjelih sigurnosnih provjera. Molimo osvježite stranicu i pokušajte ponovno ili se obratite podršci za dodatnu pomoć.',
    captcha_unavailable:
      'Registracija neuspješna zbog neuspjele provjere bota. Molimo osvježite stranicu i pokušajte ponovno ili se obratite podršci za dodatnu pomoć.',
    form_code_incorrect: 'Uneseni kod je netočan. Pokušajte ponovno.',
    form_email_address_blocked:
      'Privremene e-mail usluge nisu podržane. Molimo koristite svoju redovitu e-mail adresu za kreiranje računa.',
    form_identifier_exists__email_address: 'Ova e-mail adresa je zauzeta. Molimo pokušajte s drugom.',
    form_identifier_exists__phone_number: 'Ovaj telefonski broj je zauzet. Molimo pokušajte s drugim.',
    form_identifier_exists__username: 'Ovo korisničko ime je zauzeto. Molimo pokušajte s drugim.',
    form_identifier_not_found: 'Nismo pronašli račun s tim podacima.',
    form_new_password_matches_current: 'Nova lozinka ne može biti ista kao trenutna lozinka.',
    form_param_format_invalid: 'Neispravan format unosa.',
    form_param_format_invalid__email_address: 'E-mail adresa mora biti valjana e-mail adresa.',
    form_param_format_invalid__phone_number: 'Telefonski broj mora biti u valjanom međunarodnom formatu',
    form_param_max_length_exceeded__first_name: 'Ime ne smije biti duže od 256 znakova.',
    form_param_max_length_exceeded__last_name: 'Prezime ne smije biti duže od 256 znakova.',
    form_param_max_length_exceeded__name: 'Ime ne smije biti duže od 256 znakova.',
    form_param_nil: 'Ovo polje je obavezno i ne može biti prazno.',
    form_param_type_invalid: 'Neispravan tip unosa.',
    form_param_type_invalid__email_address: 'Ovo ne izgleda kao valjana e-mail adresa. Provjerite format.',
    form_param_type_invalid__phone_number: 'Ovo ne izgleda kao valjani telefonski broj. Provjerite format.',
    form_param_value_invalid: 'Unesena vrijednost nije valjana.',
    form_password_compromised__sign_in:
      'Ova lozinka je pronađena kao dio curenja podataka i ne može se koristiti. Molimo resetirajte svoju lozinku.',
    form_password_incorrect: 'Netočna lozinka. Pokušajte ponovno.',
    form_password_length_too_short: 'Vaša lozinka je prekratka. Mora sadržavati najmanje 8 znakova.',
    form_password_not_strong_enough: 'Vaša lozinka nije dovoljno jaka.',
    form_password_or_identifier_incorrect:
      'Lozinka ili e-mail adresa nisu točne. Pokušajte ponovno ili koristite drugu metodu.',
    form_password_pwned:
      'Ova lozinka je pronađena kao dio curenja podataka i ne može se koristiti, molimo pokušajte s drugom lozinkom.',
    form_password_pwned__sign_in:
      'Ova lozinka je pronađena kao dio curenja podataka i ne može se koristiti, molimo resetirajte svoju lozinku.',
    form_password_size_in_bytes_exceeded:
      'Vaša lozinka je premašila maksimalni dopušteni broj bajtova, molimo skratite je ili uklonite neke posebne znakove.',
    form_password_untrusted__sign_in:
      'Vaša lozinka je možda kompromitirana. Radi zaštite vašeg računa, nastavite s alternativnom metodom prijave. Nakon prijave bit će potrebno resetirati lozinku.',
    form_password_validation_failed: 'Netočna lozinka',
    form_username_invalid_character: 'Korisničko ime sadrži nevažeće znakove.',
    form_username_invalid_length: 'Vaše korisničko ime mora imati između {{min_length}} i {{max_length}} znakova.',
    form_username_needs_non_number_char: 'Vaše korisničko ime mora sadržavati najmanje jedan nebrojčani znak.',
    identification_deletion_failed: 'Ne možete izbrisati svoju posljednju identifikaciju.',
    not_allowed_access:
      "E-mail adresa ili broj telefona nije dozvoljen za registraciju. Ovo može biti zbog korištenja '+', '=', '#' ili '.' u vašoj e-mail adresi, korištenja domene povezane s vremenskom e-mail uslugom ili eksplicitnog blokiranja. Ako smatrate da je ovo pogreška, obratite se podršci.",
    organization_domain_blocked: 'Ovo je blokirana domena pružatelja e-pošte. Molimo koristite drugu.',
    organization_domain_common: 'Ovo je uobičajena domena pružatelja e-pošte. Molimo koristite drugu.',
    organization_domain_exists_for_enterprise_connection:
      'Ova domena je već registrirana za poslovnu vezu. Molimo koristite drugu domenu.',
    organization_membership_quota_exceeded:
      'Dostigli ste ograničenje članstava u organizacijama, uključujući otvorene pozivnice.',
    organization_minimum_permissions_needed:
      'Mora postojati barem jedan član organizacije s minimalnim potrebnim dozvolama.',
    organization_not_found_or_unauthorized: 'Više niste član ove organizacije. Molimo odaberite ili kreirajte drugu.',
    organization_not_found_or_unauthorized_with_create_organization_disabled:
      'Više niste član ove organizacije. Molimo odaberite drugu.',
    passkey_already_exists: 'Pristupni ključ je već registriran na ovom uređaju.',
    passkey_not_supported: 'Pristupni ključevi nisu podržani na ovom uređaju.',
    passkey_pa_not_supported: 'Registracija zahtijeva platformski autentifikator, ali uređaj ga ne podržava.',
    passkey_registration_cancelled: 'Registracija pristupnog ključa je otkazana ili je isteklo vrijeme.',
    passkey_retrieval_cancelled: 'Provjera pristupnog ključa je otkazana ili je isteklo vrijeme.',
    passwordComplexity: {
      maximumLength: 'manje od {{length}} znakova',
      minimumLength: '{{length}} ili više znakova',
      requireLowercase: 'malo slovo',
      requireNumbers: 'broj',
      requireSpecialCharacter: 'poseban znak',
      requireUppercase: 'veliko slovo',
      sentencePrefix: 'Vaša lozinka mora sadržavati',
    },
    phone_number_exists: 'Ovaj telefonski broj je zauzet. Molimo pokušajte s drugim.',
    session_exists: 'Već ste prijavljeni.',
    web3_missing_identifier: 'Nije pronađeno proširenje za Web3 novčanik. Molimo instalirajte jedno za nastavak.',
    web3_signature_request_rejected: 'Odbili ste zahtjev za potpis. Pokušajte ponovno za nastavak.',
    web3_solana_signature_generation_failed:
      'Došlo je do pogreške pri generiranju potpisa. Pokušajte ponovno za nastavak.',
    zxcvbn: {
      couldBeStronger: 'Vaša lozinka funkcionira, ali mogla bi biti jača. Pokušajte dodati više znakova.',
      goodPassword: 'Vaša lozinka zadovoljava sve potrebne zahtjeve.',
      notEnough: 'Vaša lozinka nije dovoljno jaka.',
      suggestions: {
        allUppercase: 'Napišite velika samo neka, a ne sva slova.',
        anotherWord: 'Dodajte više riječi koje su manje uobičajene.',
        associatedYears: 'Izbjegavajte godine koje su povezane s vama.',
        capitalization: 'Napišite velikim slovima više od prvog slova.',
        dates: 'Izbjegavajte datume i godine koje su povezane s vama.',
        l33t: "Izbjegavajte predvidljive zamjene slova poput '@' za 'a'.",
        longerKeyboardPattern: 'Koristite duže obrasce tipkovnice i više puta promijenite smjer tipkanja.',
        noNeed: 'Možete stvoriti jake lozinke bez korištenja simbola, brojeva ili velikih slova.',
        pwned: 'Ako ovu lozinku koristite negdje drugdje, trebali biste je promijeniti.',
        recentYears: 'Izbjegavajte nedavne godine.',
        repeated: 'Izbjegavajte ponavljanje riječi i znakova.',
        reverseWords: 'Izbjegavajte obrnuta spelovanja uobičajenih riječi.',
        sequences: 'Izbjegavajte uobičajene nizove znakova.',
        useWords: 'Koristite više riječi, ali izbjegavajte uobičajene fraze.',
      },
      warnings: {
        common: 'Ovo je često korištena lozinka.',
        commonNames: 'Uobičajena imena i prezimena lako je pogoditi.',
        dates: 'Datume je lako pogoditi.',
        extendedRepeat: 'Ponavljajuće uzorke znakova poput "abcabcabc" lako je pogoditi.',
        keyPattern: 'Kratke obrasce tipkovnice lako je pogoditi.',
        namesByThemselves: 'Pojedinačna imena ili prezimena lako je pogoditi.',
        pwned: 'Vaša lozinka je otkrivena u curenju podataka na internetu.',
        recentYears: 'Nedavne godine lako je pogoditi.',
        sequences: 'Uobičajene nizove znakova poput "abc" lako je pogoditi.',
        similarToCommon: 'Ovo je slično često korištenoj lozinci.',
        simpleRepeat: 'Ponavljajuće znakove poput "aaa" lako je pogoditi.',
        straightRow: 'Ravne redove tipki na vašoj tipkovnici lako je pogoditi.',
        topHundred: 'Ovo je često korištena lozinka.',
        topTen: 'Ovo je vrlo često korištena lozinka.',
        userInputs: 'Ne bi trebalo biti osobnih podataka ili podataka vezanih uz stranicu.',
        wordByItself: 'Pojedinačne riječi lako je pogoditi.',
      },
    },
  },
  userButton: {
    action__addAccount: 'Dodaj račun',
    action__closeUserMenu: 'Zatvori korisnički izbornik',
    action__manageAccount: 'Upravljaj računom',
    action__openUserMenu: 'Otvori korisnički izbornik',
    action__signOut: 'Odjavi se',
    action__signOutAll: 'Odjavi se sa svih računa',
  },
  userProfile: {
    apiKeysPage: {
      title: 'API ključevi',
    },
    backupCodePage: {
      actionLabel__copied: 'Kopirano!',
      actionLabel__copy: 'Kopiraj sve',
      actionLabel__download: 'Preuzmi .txt',
      actionLabel__print: 'Ispiši',
      infoText1: 'Rezervni kodovi bit će omogućeni za ovaj račun.',
      infoText2:
        'Čuvajte rezervne kodove u tajnosti i pohranite ih na sigurno. Možete regenerirati rezervne kodove ako sumnjate da su kompromitirani.',
      subtitle__codelist: 'Pohranite ih na sigurno i čuvajte u tajnosti.',
      successMessage:
        'Rezervni kodovi su sada omogućeni. Možete koristiti jedan od njih za prijavu na svoj račun ako izgubite pristup svom uređaju za autentifikaciju. Svaki kod se može koristiti samo jednom.',
      successSubtitle:
        'Možete koristiti jedan od ovih za prijavu na svoj račun ako izgubite pristup svom uređaju za autentifikaciju.',
      title: 'Dodaj verifikaciju rezervnim kodom',
      title__codelist: 'Rezervni kodovi',
    },
    billingPage: {
      paymentHistorySection: {
        empty: 'Nema povijesti plaćanja',
        notFound: 'Pokušaj plaćanja nije pronađen',
        tableHeader__amount: 'Iznos',
        tableHeader__date: 'Datum',
        tableHeader__status: 'Status',
      },
      paymentMethodsSection: {
        actionLabel__default: 'Postavi kao zadano',
        actionLabel__remove: 'Ukloni',
        add: 'Dodaj novi način plaćanja',
        addSubtitle: 'Dodajte novi način plaćanja na svoj račun.',
        cancelButton: 'Odustani',
        formButtonPrimary__add: 'Dodaj način plaćanja',
        formButtonPrimary__pay: 'Plati {{amount}}',
        payWithTestCardButton: 'Plati testnom karticom',
        removeMethod: {
          messageLine1: '{{identifier}} će biti uklonjen s ovog računa.',
          messageLine2:
            'Više nećete moći koristiti ovaj izvor plaćanja i sve ponavljajuće pretplate koje o njemu ovise više neće raditi.',
          successMessage: '{{paymentMethod}} je uklonjen s vašeg računa.',
          title: 'Ukloni način plaćanja',
        },
        title: 'Načini plaćanja',
      },
      start: {
        headerTitle__payments: 'Plaćanja',
        headerTitle__plans: 'Planovi',
        headerTitle__statements: 'Izvodi',
        headerTitle__subscriptions: 'Pretplata',
      },
      statementsSection: {
        empty: 'Nema izvoda za prikaz',
        itemCaption__paidForPlan: 'Plaćeno za {{plan}} {{period}} plan',
        itemCaption__proratedCredit: 'Proporcionalni kredit za djelomično korištenje prethodne pretplate',
        itemCaption__payerCredit: 'Kredit sa stanja računa',
        itemCaption__subscribedAndPaidForPlan: 'Pretplaćeno i plaćeno za {{plan}} {{period}} plan',
        notFound: 'Izvod nije pronađen',
        tableHeader__amount: 'Iznos',
        tableHeader__date: 'Datum',
        title: 'Izvodi',
        totalPaid: 'Ukupno plaćeno',
      },
      subscriptionsListSection: {
        actionLabel__manageSubscription: 'Upravljaj',
        actionLabel__newSubscription: 'Pretplatite se na plan',
        actionLabel__switchPlan: 'Promijeni plan',
        tableHeader__edit: 'Uredi',
        tableHeader__plan: 'Plan',
        tableHeader__startDate: 'Datum početka',
        title: 'Pretplata',
      },
      subscriptionsSection: {
        actionLabel__default: 'Upravljaj',
      },
      switchPlansSection: {
        title: 'Promijeni plan',
      },
      title: 'Naplata',
    },
    connectedAccountPage: {
      formHint: 'Odaberite pružatelja usluge za povezivanje vašeg računa.',
      formHint__noAccounts: 'Nema dostupnih vanjskih pružatelja računa.',
      removeResource: {
        messageLine1: '{{identifier}} će biti uklonjen s ovog računa.',
        messageLine2: 'Više nećete moći koristiti ovaj povezani račun i sve ovisne značajke više neće raditi.',
        successMessage: '{{connectedAccount}} je uklonjen s vašeg računa.',
        title: 'Ukloni povezani račun',
      },
      socialButtonsBlockButton: '{{provider|titleize}}',
      successMessage: 'Pružatelj usluge je dodan vašem računu',
      title: 'Dodaj povezani račun',
    },
    deletePage: {
      actionDescription: 'Upišite "Izbriši račun" ispod za nastavak.',
      confirm: 'Izbriši račun',
      messageLine1:
        'Jeste li sigurni da želite izbrisati svoj račun? Neki povezani podaci mogu biti zadržani. Za zahtjev potpunog brisanja podataka, molimo kontaktirajte podršku.',
      messageLine2: 'Ova radnja je trajna i nepovratna.',
      title: 'Izbriši račun',
    },
    emailAddressPage: {
      emailCode: {
        formHint: 'Poruka s verifikacijskim kodom bit će poslana na ovu adresu e-pošte.',
        formSubtitle: 'Unesite verifikacijski kod poslan na {{identifier}}',
        formTitle: 'Verifikacijski kod',
        resendButton: 'Niste primili kod? Pošalji ponovno',
        successMessage: 'E-pošta {{identifier}} je dodana vašem računu.',
      },
      emailLink: {
        formHint: 'Poruka s verifikacijskom poveznicom bit će poslana na ovu adresu e-pošte.',
        formSubtitle: 'Kliknite na verifikacijsku poveznicu u e-pošti poslanoj na {{identifier}}',
        formTitle: 'Verifikacijska poveznica',
        resendButton: 'Niste primili poveznicu? Pošalji ponovno',
        successMessage: 'E-pošta {{identifier}} je dodana vašem računu.',
      },
      enterpriseSSOLink: {
        formButton: 'Kliknite za prijavu',
        formSubtitle: 'Dovršite prijavu s {{identifier}}',
      },
      formHint: 'Trebat ćete potvrditi ovu e-mail adresu prije nego što se može dodati vašem računu.',
      removeResource: {
        messageLine1: '{{identifier}} će biti uklonjen s ovog računa.',
        messageLine2: 'Više se nećete moći prijaviti koristeći ovu adresu e-pošte.',
        successMessage: '{{emailAddress}} je uklonjena s vašeg računa.',
        title: 'Ukloni adresu e-pošte',
      },
      title: 'Dodaj adresu e-pošte',
      verifyTitle: 'Potvrdi adresu e-pošte',
    },
    formButtonPrimary__add: 'Dodaj',
    formButtonPrimary__continue: 'Nastavi',
    formButtonPrimary__finish: 'Završi',
    formButtonPrimary__remove: 'Ukloni',
    formButtonPrimary__save: 'Spremi',
    formButtonReset: 'Odustani',
    mfaPage: {
      formHint: 'Odaberite metodu za dodavanje.',
      title: 'Dodaj dvostupanjsku provjeru',
    },
    mfaPhoneCodePage: {
      backButton: 'Koristi postojeći broj',
      primaryButton__addPhoneNumber: 'Dodaj telefonski broj',
      removeResource: {
        messageLine1: '{{identifier}} više neće primati verifikacijske kodove prilikom prijave.',
        messageLine2: 'Vaš račun možda neće biti toliko siguran. Jeste li sigurni da želite nastaviti?',
        successMessage: 'SMS kod dvostupanjske provjere je uklonjen za {{mfaPhoneCode}}',
        title: 'Ukloni dvostupanjsku provjeru',
      },
      subtitle__availablePhoneNumbers:
        'Odaberite postojeći telefonski broj za registraciju SMS koda dvostupanjske provjere ili dodajte novi.',
      subtitle__unavailablePhoneNumbers:
        'Nema dostupnih telefonskih brojeva za registraciju SMS koda dvostupanjske provjere, molimo dodajte novi.',
      successMessage1:
        'Prilikom prijave, morat ćete unijeti verifikacijski kod poslan na ovaj telefonski broj kao dodatni korak.',
      successMessage2:
        'Spremite ove rezervne kodove i pohranite ih na sigurno mjesto. Ako izgubite pristup svom uređaju za autentifikaciju, možete koristiti rezervne kodove za prijavu.',
      successTitle: 'SMS kod verifikacije omogućen',
      title: 'Dodaj SMS kod verifikacije',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: 'Umjesto toga, skeniraj QR kod',
        buttonUnableToScan__nonPrimary: 'Ne možete skenirati QR kod?',
        infoText__ableToScan:
          'Postavite novu metodu prijave u vašoj aplikaciji za autentifikaciju i skenirajte sljedeći QR kod da biste je povezali s vašim računom.',
        infoText__unableToScan: 'Postavite novu metodu prijave u vašem autentifikatoru i unesite Ključ naveden ispod.',
        inputLabel__unableToScan1:
          'Provjerite jesu li omogućene vremenski temeljene ili jednokratne lozinke, zatim završite povezivanje vašeg računa.',
        inputLabel__unableToScan2:
          'Alternativno, ako vaš autentifikator podržava TOTP URI-je, možete također kopirati cijeli URI.',
      },
      removeResource: {
        messageLine1: 'Verifikacijski kodovi iz ovog autentifikatora više neće biti potrebni prilikom prijave.',
        messageLine2: 'Vaš račun možda neće biti toliko siguran. Jeste li sigurni da želite nastaviti?',
        successMessage: 'Dvostupanjska provjera putem aplikacije za autentifikaciju je uklonjena.',
        title: 'Ukloni dvostupanjsku provjeru',
      },
      successMessage:
        'Dvostupanjska provjera je sada omogućena. Prilikom prijave, morat ćete unijeti verifikacijski kod iz ovog autentifikatora kao dodatni korak.',
      title: 'Dodaj aplikaciju za autentifikaciju',
      verifySubtitle: 'Unesite verifikacijski kod generiran vašim autentifikatorom',
      verifyTitle: 'Verifikacijski kod',
    },
    mobileButton__menu: 'Izbornik',
    navbar: {
      account: 'Profil',
      apiKeys: 'API ključevi',
      billing: 'Naplata',
      description: 'Upravljajte informacijama vašeg računa.',
      security: 'Sigurnost',
      title: 'Račun',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: '{{name}} će biti uklonjen s ovog računa.',
        title: 'Ukloni pristupni ključ',
      },
      subtitle__rename: 'Možete promijeniti naziv pristupnog ključa kako biste ga lakše pronašli.',
      title__rename: 'Preimenuj pristupni ključ',
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'Preporučuje se odjava sa svih drugih uređaja koji su možda koristili vašu staru lozinku.',
      readonly: 'Vaša lozinka trenutno se ne može uređivati jer se možete prijaviti samo putem poslovne veze.',
      successMessage__set: 'Vaša lozinka je postavljena.',
      successMessage__signOutOfOtherSessions: 'Svi ostali uređaji su odjavljeni.',
      successMessage__update: 'Vaša lozinka je ažurirana.',
      title__set: 'Postavi lozinku',
      title__update: 'Ažuriraj lozinku',
    },
    phoneNumberPage: {
      infoText:
        'Tekstualna poruka s verifikacijskim kodom bit će poslana na ovaj telefonski broj. Mogu se primijeniti naknade za poruke i podatke.',
      removeResource: {
        messageLine1: '{{identifier}} će biti uklonjen s ovog računa.',
        messageLine2: 'Više se nećete moći prijaviti koristeći ovaj telefonski broj.',
        successMessage: '{{phoneNumber}} je uklonjen s vašeg računa.',
        title: 'Ukloni telefonski broj',
      },
      successMessage: '{{identifier}} je dodan vašem računu.',
      title: 'Dodaj telefonski broj',
      verifySubtitle: 'Unesite verifikacijski kod poslan na {{identifier}}',
      verifyTitle: 'Potvrdi telefonski broj',
    },
    plansPage: {
      title: 'Planovi',
    },
    profilePage: {
      fileDropAreaHint: 'Preporučena veličina 1:1, do 10MB.',
      imageFormDestructiveActionSubtitle: 'Ukloni',
      imageFormSubtitle: 'Učitaj',
      imageFormTitle: 'Profilna slika',
      readonly: 'Vaše profilne informacije su pružene putem poslovne veze i ne mogu se uređivati.',
      successMessage: 'Vaš profil je ažuriran.',
      title: 'Ažuriraj profil',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: 'Odjavi se s uređaja',
        title: 'Aktivni uređaji',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: 'Ponovno poveži',
        actionLabel__reauthorize: 'Autoriziraj sada',
        destructiveActionTitle: 'Ukloni',
        primaryButton: 'Poveži račun',
        subtitle__disconnected: 'Ovaj račun je isključen.',
        subtitle__reauthorize:
          'Potrebna ovlaštenja su ažurirana i možda doživljavate ograničenu funkcionalnost. Molimo vas da ponovno autorizirate ovu aplikaciju kako biste izbjegli probleme',
        title: 'Povezani računi',
      },
      dangerSection: {
        deleteAccountButton: 'Izbriši račun',
        title: 'Izbriši račun',
      },
      emailAddressesSection: {
        destructiveAction: 'Ukloni e-poštu',
        detailsAction__nonPrimary: 'Postavi kao primarnu',
        detailsAction__primary: 'Dovrši verifikaciju',
        detailsAction__unverified: 'Potvrdi',
        primaryButton: 'Dodaj adresu e-pošte',
        title: 'Adrese e-pošte',
      },
      enterpriseAccountsSection: {
        primaryButton: 'Poveži račun',
        title: 'Poslovni računi',
      },
      headerTitle__account: 'Detalji profila',
      headerTitle__security: 'Sigurnost',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: 'Regeneriraj',
          headerTitle: 'Rezervni kodovi',
          subtitle__regenerate:
            'Dobijte novi set sigurnosnih rezervnih kodova. Prethodni rezervni kodovi bit će izbrisani i ne mogu se koristiti.',
          title__regenerate: 'Regeneriraj rezervne kodove',
        },
        phoneCode: {
          actionLabel__setDefault: 'Postavi kao zadano',
          destructiveActionLabel: 'Ukloni',
        },
        primaryButton: 'Dodaj dvostupanjsku provjeru',
        title: 'Dvostupanjska provjera',
        totp: {
          destructiveActionTitle: 'Ukloni',
          headerTitle: 'Aplikacija za autentifikaciju',
        },
      },
      passkeysSection: {
        menuAction__destructive: 'Ukloni',
        menuAction__rename: 'Preimenuj',
        primaryButton: 'Dodaj pristupni ključ',
        title: 'Pristupni ključevi',
      },
      passwordSection: {
        primaryButton__setPassword: 'Postavi lozinku',
        primaryButton__updatePassword: 'Ažuriraj lozinku',
        title: 'Lozinka',
      },
      phoneNumbersSection: {
        destructiveAction: 'Ukloni telefonski broj',
        detailsAction__nonPrimary: 'Postavi kao primarni',
        detailsAction__primary: 'Dovrši verifikaciju',
        detailsAction__unverified: 'Potvrdi telefonski broj',
        primaryButton: 'Dodaj telefonski broj',
        title: 'Telefonski brojevi',
      },
      profileSection: {
        primaryButton: 'Ažuriraj profil',
        title: 'Profil',
      },
      usernameSection: {
        primaryButton__setUsername: 'Postavi korisničko ime',
        primaryButton__updateUsername: 'Ažuriraj korisničko ime',
        title: 'Korisničko ime',
      },
      web3WalletsSection: {
        destructiveAction: 'Ukloni novčanik',
        detailsAction__nonPrimary: 'Postavi kao primarni',
        primaryButton: 'Poveži novčanik',
        title: 'Web3 novčanici',
        web3SelectSolanaWalletScreen: {
          subtitle: 'Odaberite Solana novčanik za povezivanje s vašim računom.',
          title: 'Dodaj Solana novčanik',
        },
      },
    },
    usernamePage: {
      successMessage: 'Vaše korisničko ime je ažurirano.',
      title__set: 'Postavi korisničko ime',
      title__update: 'Ažuriraj korisničko ime',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}} će biti uklonjen s ovog računa.',
        messageLine2: 'Više se nećete moći prijaviti koristeći ovaj web3 novčanik.',
        successMessage: '{{web3Wallet}} je uklonjen s vašeg računa.',
        title: 'Ukloni web3 novčanik',
      },
      subtitle__availableWallets: 'Odaberite web3 novčanik za povezivanje s vašim računom.',
      subtitle__unavailableWallets: 'Nema dostupnih web3 novčanika.',
      successMessage: 'Novčanik je dodan vašem računu.',
      title: 'Dodaj web3 novčanik',
      web3WalletButtonsBlockButton: '{{provider|titleize}}',
    },
  },
  waitlist: {
    start: {
      actionLink: 'Prijavi se',
      actionText: 'Već imaš pristup?',
      formButton: 'Pridruži se listi čekanja',
      subtitle: 'Unesi svoju e-mail adresu i obavijestit ćemo te kada tvoje mjesto bude spremno',
      title: 'Pridruži se listi čekanja',
    },
    success: {
      message: 'Uskoro ćeš biti preusmjeren...',
      subtitle: 'Javit ćemo ti se kada tvoje mjesto bude spremno',
      title: 'Hvala što si se pridružio/la listi čekanja!',
    },
  },
  web3SolanaWalletButtons: {
    connect: 'Poveži se s {{walletName}}',
    continue: 'Nastavi s {{walletName}}',
    noneAvailable:
      'Nisu otkrivene Solana Web3 novčanike. Instalirajte {{ solanaWalletsLink || link("wallet extension") }} s podrškom za Web3.',
  },
} as const;
